import React, {useContext, useEffect, useState} from 'react';
import {Card, Col} from "react-bootstrap";
import Row from "react-bootstrap/Row";
import {Context} from "../../index";
import {useNavigate} from "react-router-dom";
import {
    fetchAssignmentsCountForUser, fetchGlobalSettings,
} from "../../http/bookAPI";
import Loader from "../Loader";
import UsersByCountryPie from "./UsersByCountryPie";
import DashboardActivity from "./DashboardActivity";
import DiscountBonusOffers from "./DiscountBonusOffers";
import ColRdpAccess from "../columns/ColRdpAccess";
import DashboardPromotionCard from "./DashboardPromotionCard";

const getDashboardPromotion = (settings) => (
    settings?.dashboard_promotion ||
    settings?.promotions_config?.dashboard_promotion ||
    null
);

const DashBoardMain = () => {
    const { user, affiliateStore } = useContext(Context);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const isMobile = window.innerWidth < 560;
    const [reviewerData, setReviewerData] = useState({});
    const [settings, setSettings] = useState(null);
    const dashboardPromotion = getDashboardPromotion(settings);

    const loadSettings = async () => {
        try {
            const data = await fetchGlobalSettings();
            setSettings(data ?? null);
            return data ?? null;
        } catch {
            setSettings(null);
            return null;
        }
    };

    const handleClick = () => {
        navigate("/faq");
    };

    useEffect(() => {
        let canceled = false;
        setLoading(true);

        (async () => {
            try {
                const [reviewer] = await Promise.all([
                    fetchAssignmentsCountForUser(user?.user?.id),
                    loadSettings(),
                ]);
                if (canceled) return;

                setReviewerData(reviewer);
                user.setReviewerData(reviewer);

            } finally {
                if (!canceled) setLoading(false);
            }
        })();

        return () => { canceled = true; };
    }, [user, user?.user?.id]);


   const nowInMinutes = Math.floor(Date.now() / 60000);


    return (

        <div>
            {loading ? (
                <Loader />
            ) : (
                <>
                    {(nowInMinutes - user?.user?.registration_date) <= 360 && (
                        <>
                            <Card className="rounded shadow-lg mt-2">
                                <Row className="g-2 p-3 small">
                                    <Col xs={12}>
                                        <h5 className="text-muted fst-italic">
                                            Welcome to your Dashboard, {user?.user?.username}!
                                        </h5>
                                        <p className="text-muted mb-2">
                                            <strong>
                                                This message will disappear in{" "}
                                                {Math.floor((user?.user?.registration_date + 360 - nowInMinutes) / 60)} hours{" "}
                                                {Math.floor((user?.user?.registration_date + 360 - nowInMinutes) % 60)} minutes
                                            </strong>
                                            , but for now, let's go over the basics.
                                        </p>
                                    </Col>

                                    <Col xs={12}>
                                        <h5 className="text-muted fst-italic mb-1">
                                            How Does the Virtalibry System Work?
                                        </h5>
                                        <p className="text-muted mb-2">
                                            Your goal is simple: Earn Virt by completing review assignments. Then, use Virt to create assignments for your own books.
                                        </p>
                                    </Col>

                                    <Col xs={12}>
                                        <h5 className="text-muted fst-italic mb-1">What’s in Your Dashboard?</h5>
                                        <ul className="text-muted ps-4">
                                            <li><strong>Right Side Column - "Your Books":</strong> Your books in the Virtalibry System (once you add them).</li>
                                            <li><strong>Left Side Column - "Assignments":</strong> Assignments you’ve taken to earn Virt.</li>
                                            <li><strong>Center Column:</strong> "Your Customer Details" and "Score & Subscription Details," plus buttons to manage your profile settings and subscription.</li>
                                            <li><strong>Center Column Bottom:</strong> "Current and Upcoming Discount & Bonus Offers." Discounts apply when creating assignments, and bonuses are earned by taking assignments from the library.</li>
                                            <li><strong>Center Column Bottom:</strong> "Financial & Trust Level Trends Graph" (30 or 120-day view). It shows changes in your balance, trust level, spending, and earnings.</li>
                                        </ul>
                                    </Col>

                                    <Col xs={12}>
                                        <h5 className="text-muted fst-italic mb-1">Polls: Get Real Feedback & Earn Virt</h5>
                                        <ul className="text-muted ps-4">
                                            <li>
                                                <strong>Take part in polls:</strong> Earn Virt by answering simple questions about book covers, titles, or descriptions.
                                            </li>
                                            <li>
                                                <strong>Create your own polls:</strong> Want to know which book cover, title, or blurb performs best? Launch your poll and get real data from real readers.
                                            </li>
                                            <li>
                                                <strong>Fast & visual feedback:</strong> Results are compiled into charts so you can instantly see the winner.
                                            </li>
                                        </ul>
                                    </Col>

                                    <Col xs={12}>
                                        <h5 className="text-muted fst-italic mb-1">Your First Steps:</h5>
                                        <ol className="text-muted ps-4">
                                            <li>
                                                <strong>Set up your Amazon Reviewer Account:</strong> Go to Settings → Profile and add your Amazon Reviewer details. This is required to take assignments from the library and will display the name under which you post reviews.
                                            </li>
                                            <li>
                                                <strong>Take your first assignment:</strong> Visit the Library and pick an assignment to start earning Virt.
                                            </li>
                                            <li>
                                                <strong>Create or test your book:</strong> Click "Add Book" in the left section or launch a poll to test your cover or title.
                                            </li>
                                        </ol>
                                    </Col>

                                    <Col xs={12}>
                                        <p className="text-muted">
                                            If you have any questions, check out the{" "}
                                            <span className="text-primary" style={{ cursor: "pointer" }} onClick={handleClick}>
              FAQ
            </span>{" "}
                                            or contact support using the chat button at the bottom right. Now, let's get started!
                                        </p>
                                    </Col>
                                </Row>
                            </Card>
                        </>
                    )}

                    <DashboardPromotionCard
                        promotion={dashboardPromotion}
                        onOpen={() => user?.setActiveComponent("DashboardPromotion")}
                    />

                    <Card className="p-3 w-100 mt-2">
                        <div className="d-flex flex-column flex-md-row flex-md-nowrap justify-content-between align-items-start align-items-md-center gap-2">
                            <h5 className="text-muted mb-2 mb-md-0 me-md-3 text-nowrap">
                                Account & Performance
                            </h5>

                            <div className="
    d-flex
    flex-wrap
    gap-2
    align-items-center

">

                                <h6 className="text-muted mb-0 me-2 flex-shrink-0 d-none d-md-block">
                                    Full Service Overview:
                                </h6>

                                {/* Mobile label */}
                                <h6 className="text-muted mb-0 me-2 flex-shrink-0 d-block d-md-none">
                                    Intro:
                                </h6>
                                <a
                                    href="https://docs.google.com/presentation/d/1Si5rO6Kp3p985hcs-gf6u_03fZ2rvVNrlGwhyRH7s20/edit?usp=sharing"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-2 py-1 bg-light rounded text-decoration-none small fw-semibold border border-secondary"
                                >
                                    EN
                                </a>

                                <a
                                    href="https://docs.google.com/presentation/d/1ZiUlW1j0s6reJ1xpFc0tS6ZgxyL03fK0rBGDoB6hHvc/edit?usp=sharing"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-2 py-1 bg-light rounded text-decoration-none small fw-semibold border border-secondary"
                                >
                                    ES
                                </a>

                                <a
                                    href="https://docs.google.com/presentation/d/1z12FK29Nyb888uKSJmXsROk2JryyXNoAHrM-IZmCsgQ/edit?usp=sharing"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-2 py-1 bg-light rounded text-decoration-none small fw-semibold border"
                                >
                                    PT
                                </a>

                                <a
                                    href="https://docs.google.com/presentation/d/1qY6gP39og8HIHeWdz7MFoCmsGu1L4oQ7T-zCWNnI_Mw/edit?usp=sharing"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-2 py-1 bg-light rounded text-decoration-none small fw-semibold border"
                                >
                                    DE
                                </a>
                                {/*
                                <a
                                    href="https://docs.google.com/presentation/d/12yqm94pnVDoWuGJVTXJEJyglORquAvppEa_I_HVvXy4/edit?usp=sharing"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-2 py-1 bg-light rounded text-decoration-none small fw-semibold border"
                                >
                                    FR
                                </a>*/}

                                <a
                                    href="https://docs.google.com/presentation/d/1iZa2GNXKh7IJyGx7oSCVafPv4k1b9uDsovcEVZYNWX0/edit?usp=sharing"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-2 py-1 bg-light rounded text-decoration-none small fw-semibold border"
                                >
                                    IT
                                </a>

                                <a
                                    href="https://docs.google.com/presentation/d/18feLIR3COhLuNE_6ePtDbXgLz6YrQeduu0AEtiYfQco/edit?usp=sharing"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-2 py-1 bg-light rounded text-decoration-none small fw-semibold border border-secondary"
                                >
                                    RU
                                </a>

                            </div>


                        </div>

                        <div className="mt-2">
                            {/* grid: 3 колонки на десктопе, стэк на мобиле */}
                            <div className="row g-3">
                            <div className="col-12 col-lg-4">
                                <Card className="h-100 border-secondary">
                                    <Card.Header className="py-1">Customer Details</Card.Header>
                                    <Card.Body className="p-2">
                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Username</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="fw-semibold text-truncate text-end">
    {user?.user?.username || "—"}
  </span>
                                        </div>

                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Email</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="text-truncate text-end">
    {user?.user?.email || "—"}
  </span>
                                        </div>

                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Registration</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="text-truncate text-end">
    {(() => {
        const ms = (user?.user?.registration_date ?? 0) * 60000;
        if (!ms) return "—";
        const d = new Date(ms);
        return d.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    })()}
  </span>
                                        </div>

                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Amazon Name</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="fw-semibold text-truncate text-end">
    {user?.user?.amz_name?.trim() || "Fill in → Profile"}
  </span>
                                        </div>

                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Amazon Site</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="fw-semibold text-truncate text-end">
    {user?.user?.amz_country?.trim() || "Fill in → Profile"}
  </span>
                                        </div>

                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Audible Name</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="fw-semibold text-truncate text-end">
    {user?.user?.audible_name?.trim() || "Fill in → Profile"}
  </span>
                                        </div>

                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Audible Site</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="fw-semibold text-truncate text-end">
    {user?.user?.audible_country?.trim() || "Fill in → Profile"}
  </span>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>

                            {/* 2) Subscription */}
                            <div className="col-12 col-lg-4">
                                <Card className="h-100 border-secondary">
                                    <Card.Header className="py-1">Subscription & Balances</Card.Header>
                                    <Card.Body className="p-2">
                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Subscription</span>
                                            <div className="flex-grow-1 border-bottom  mx-1"></div>
                                            <span className="fw-semibold text-success text-end">
    {user?.user?.tariff_plan}
  </span>
                                        </div>

                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Status</span>
                                            <div className="flex-grow-1 border-bottom  mx-1 "></div>
                                            <span
                                                className={
                                                    "fw-semibold text-end " +
                                                    (user?.user?.stripe_subscription_status === "Past Due"
                                                        ? "blink-danger"
                                                        : "text-success")
                                                }
                                            >
    {(() => {
        const plan = user?.user?.tariff_plan || "FREE";
        if (plan === "FREE") return "Active";

        const status = user?.user?.stripe_subscription_status;
        const end = user?.user?.stripe_subscription_end_date
            ? new Date(user.user.stripe_subscription_end_date)
            : null;
        const now = new Date();
        let daysLeft = 0;

        // Expired или Past Due → 7 дней после end
        if ((status === "Past Due") && end) {
            const cancelDate = new Date(end.getTime() + 7 * 86400000);
            daysLeft = Math.max(0, Math.ceil((cancelDate - now) / 86400000));
        } else if (status === "Expired")  {
            daysLeft = '';
        } else if (end) {
            // Active → до end
            daysLeft = Math.max(0, Math.ceil((end - now) / 86400000));
        }

        let label;
        if (status === "Active") {
            label = "Charged in";
        } else if (status === "Expired") {
            label = "No";
        } else if (status === "Past Due") {
            label = "Cancel in";
        } else {
            label = "Expired in";
        }

        const dayLabel = daysLeft === 1 ? "Day" : "Days";
        return `${status} (${label} ${daysLeft} ${dayLabel})`;
    })()}
</span>

                                        </div>


                                        <div className="d-flex align-items-baseline small">
  <span className="me-0">
    {user?.user?.tariff_plan === "FREE" ? "Subscription Start" : "Subscription Start"}
  </span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="text-end">
    {(() => {
        const isFree = user?.user?.tariff_plan === "FREE";
        const ts = isFree
            ? (user?.user?.registration_date ?? 0) * 60000
            : user?.user?.stripe_subscription_start_date;
        if (!ts) return "—";
        return new Date(ts).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    })()}
  </span>
                                        </div>

                                        <div className="d-flex align-items-baseline small">
  <span className="me-0">
    {user?.user?.tariff_plan === "FREE" ? "Subscription End" : "Subscription End"}
  </span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="text-end">
    {user?.user?.tariff_plan === "FREE"
        ? "Never end"
        : (() => {
            const ts = user?.user?.stripe_subscription_end_date;
            if (!ts) return "—";
            return new Date(ts).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "long",
                year: "numeric",
            });
        })()}
  </span>
                                        </div>

                                        {/* Virt Balance */}
                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Virt Balance</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="fw-semibold text-success text-end">
    {Number(user?.user?.balance ?? 0)} Virt
  </span>
                                        </div>

                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">USD Balance</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="fw-semibold text-success text-end">
    ${Number(user?.user?.lr_balance ?? 0).toFixed(2)}
  </span>
                                        </div>

                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Affiliate Balance</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="fw-semibold text-success text-end">
    ${Number(user?.user?.affiliate_balance ?? 0).toFixed(2)}
  </span>
                                        </div>

                                    </Card.Body>
                                </Card>
                            </div>

                            {/* 3) Ratings & Balances */}
                            <div className="col-12 col-lg-4">
                                <Card className="h-100 border-secondary">
                                    <Card.Header className="py-1">Ratings & Score </Card.Header>
                                    <Card.Body className="p-2">
                                        {/* Book Limit / Rating */}
                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Your Books Rating</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="text-end">
    {reviewerData?.averageBookRating ?? "–"}{" "}
                                                {[...Array(5)].map((_, i) => {
                                                    const r = reviewerData?.averageBookRating || 0;
                                                    const icon =
                                                        i + 1 <= r ? "star-fill" : i + 0.5 <= r ? "star-half" : "star";
                                                    return (
                                                        <i
                                                            key={i}
                                                            className={`bi bi-${icon}`}
                                                            style={{ fontSize: 14, marginLeft: 2, color: "gold" }}
                                                        />
                                                    );
                                                })}{" "}
                                                ({reviewerData?.bookRatingsCount || 0})
  </span>
                                        </div>

                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Expanse for Books by Plan</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="text-end">
    {(
        {
            FREE: "One",
            ESSENTIAL: "Five",
            PREMIUM: "Fifty",
            MAXIMUM: "500",
        }[user?.user?.tariff_plan] || "–"
    )}
  </span>
                                        </div>


                                        {/* Assign Limit / Rating */}
                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Reviewed Books Rating</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="text-end">
    {reviewerData?.averageAssignRating ?? "–"}{" "}
                                                {[...Array(5)].map((_, i) => {
                                                    const r = reviewerData?.averageAssignRating || 0;
                                                    const icon =
                                                        i + 1 <= r ? "star-fill" : i + 0.5 <= r ? "star-half" : "star";
                                                    return (
                                                        <i
                                                            key={i}
                                                            className={`bi bi-${icon}`}
                                                            style={{ fontSize: 14, marginLeft: 2, color: "gold" }}
                                                        />
                                                    );
                                                })}{" "}
                                                ({reviewerData?.assignRatingsCount || 0})
  </span>
                                        </div>

                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Expanse for Assignments · Plan/Now</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="text-end">
    {user?.user?.assignment_limit ?? "–"} / {(() => {
                                                const plan = String(user?.user?.tariff_plan || "").toUpperCase();
                                                const s = settings || {};
                                                return (
                                                    {
                                                        FREE: s.free_user_max_active_ass,
                                                        ESSENTIAL: s.essential_user_max_active_ass,
                                                        PREMIUM: s.premium_user_max_active_ass,
                                                        MAXIMUM: s.maximum_user_max_active_ass,
                                                    }[plan] ?? "—"
                                                );
                                            })()}
  </span>
                                        </div>


                                        {/* Reviews Quality */}
                                        <div className="d-flex align-items-baseline small">
                                            <span title={`Reviews Quality: ${reviewerData?.reviewQuality100}% (Confirmed / Complaint)`}
                                             className="me-0">Reviews Quality %</span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="text-end">

<span className="ms-1 fw-semibold">{reviewerData?.reviewQuality100}%{" "}</span>
                                                ({reviewerData?.reviewerCompleteCount ?? "–"} / {reviewerData?.reviewerComplaintCount ?? "–"})
                                                 <i
                                                     className={`bi ${
                                                         reviewerData?.reviewQuality100 > 80
                                                             ? "bi-battery-full text-success"
                                                             : reviewerData?.reviewQuality100 > 50
                                                                 ? "bi-battery-half text-warning"
                                                                 : "bi-battery text-danger"
                                                     } fs-6`}
                                                     style={{ display: "inline-block", transform: "rotate(270deg)" }}
                                                     title={`Reviews Quality: ${reviewerData?.reviewQuality100}% (Confirmed / Complaint)`}
                                                 />
  </span>
                                        </div>
                                        {/* Reviews Quality */}
                                        <div className="d-flex align-items-baseline small">
                                            <span title="Complaint Last 30 Days" className="me-0">Complaint · Last 30 Days / Total </span>
                                            <div className="flex-grow-1 mx-1 border-bottom"></div>
                                            <span className="text-end">
                                                {reviewerData?.reviewerComplaintCount30Day ?? "–"} / {reviewerData?.reviewerComplaintCount ?? "–"}
  </span>
                                        </div>


                                        {/* Trust Score */}
                                        <div className="d-flex align-items-baseline small">
                                            <span className="me-0">Your Trust Score</span>
                                            <div className="flex-grow-1 border-bottom mx-1"></div>
                                            <span className="text-end">
    {user?.user?.level_of_trust ?? "—"} Points
  </span>
                                        </div>

                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                            {affiliateStore?.referrerData?.from_referrer_name && (
                                <div className="row mt-0 g-3">
                                    <div className="col-12 col-lg-12">
                                        <Card className="h-100 border-secondary">
                                            <Card.Header className="py-1">
                                                Referred By: <span className="fw-semibold">{affiliateStore.referrerData.from_referrer_name}</span>
                                            </Card.Header>

                                            <Card.Body className="p-2">

                                                {affiliateStore?.referrerData?.from_referrer_message && (
                                                    <div className="small">
                                                        <span className="fw-semibold">Message: </span>
                                                        <span>{affiliateStore.referrerData.from_referrer_message}</span>
                                                    </div>
                                                )}

                                            </Card.Body>
                                        </Card>
                                    </div>
                                </div>
                            )}

                        </div>
                    </Card>


                    {/*        <Card className=" rounded shadow-lg w-100 mt-2">
                    <Row className="g-0 ">
                <>

                <Col md={6} className="mt-0 p-3">

                    <h5 className="text-muted fst-italic">Your Customer Details:</h5>
                    <Table striped bordered size="sm" className="align-middle" style={{ maxWidth: '100%', lineHeight: '14px' }}>
                        <tbody>
                        <tr>
                            <td className="text-start  small text-muted fst-italic">Username:</td>
                            <td className="text-start  small text-muted">{user?.user?.username}</td>
                        </tr>
                        <tr>
                            <td className="text-start  small text-muted fst-italic">Email:</td>
                            <td className="text-start  small text-muted">{user?.user?.email}</td>
                        </tr>
                        <tr>
                            <td className="text-start  small text-muted fst-italic">Registration:</td>
                            <td className="text-start  small text-muted">{new Date(user?.user?.registration_date * 60 * 1000).toLocaleString("en-US", {
                                month: "2-digit", day: "2-digit", year: "numeric",
                                hour: "2-digit", minute: "2-digit", hour12: true
                            })}</td>
                        </tr>
                        <tr>
                            <td className="text-start  small text-muted fst-italic">Amazon Reviewer Name:</td>
                            <td className="text-start  small text-muted">
                                {user?.user?.amz_name && user?.user?.amz_name.trim() !== ""
                                    ? user?.user?.amz_name
                                    : "Fill in Settings -> Profile"}
                            </td>
                        </tr>

                        <tr>
                            <td className="text-start  small text-muted fst-italic">Amazon Country Site:</td>
                            <td className="text-start  small text-muted">
                                {user?.user?.amz_country && user?.user?.amz_country.trim() !== ""
                                    ? user?.user?.amz_country
                                    : "Fill in Settings -> Profile"}
                            </td>
                        </tr>

                        <tr>
                            <td className="text-start  small text-muted fst-italic">Audible Reviewer Name:</td>
                            <td className="text-start  small text-muted">
                                {user?.user?.audible_name && user?.user?.audible_name.trim() !== ""
                                    ? user?.user?.audible_name
                                    : "Fill in Settings -> Profile"}
                            </td>
                        </tr>

                        <tr>
                            <td className="text-start  small text-muted fst-italic">Audible Country Site:</td>
                            <td className="text-start  small text-muted">
                                {user?.user?.audible_country && user?.user?.audible_country.trim() !== ""
                                    ? user?.user?.audible_country
                                    : "Fill in Settings -> Profile"}
                            </td>
                        </tr>
                        {affiliateStore?.referrerData?.from_referrer_name && (<>
                        <tr>
                            <td className="text-start  small text-muted fst-italic">You were invited by:</td>
                            <td className="text-start  small text-muted">
                                {affiliateStore?.referrerData?.from_referrer_name}
                            </td>
                        </tr>
                        </>)}
                        {affiliateStore?.referrerData?.from_referrer_message && (<>
                        <tr>
                            <td className="text-start  small text-muted fst-italic">Message from {affiliateStore?.referrerData?.from_referrer_name}:</td>
                            <td className="text-start  small text-muted">
                                {affiliateStore?.referrerData?.from_referrer_message}
                            </td>
                        </tr>
                        </>)}


                        </tbody>
                    </Table>

                </Col>


                <Col md={6} className={`mt-0 p-3 ${isMobile ? 'small' : ''}`}
                >

                    <h5 className="text-muted fst-italic">Score & Subscription Details:</h5>
                    <Table striped bordered size="sm" className="align-middle" style={{ maxWidth: '100%', lineHeight: '16px' }}>
                        <tbody>
                        <tr>
                            <td className="text-start  small text-muted fst-italic">Tariff plan:</td>
                            <td className="text-start  small text-success ">
                                {user?.user?.tariff_plan}
                                {!isMobile && (<> (
                                {user?.user?.tariff_plan === "FREE"
                                    ? "Forever Free"
                                    : (() => {
                                        const now = Date.now(); // Текущее время в миллисекундах
                                        const endDate = new Date(user?.user?.stripe_subscription_end_date).getTime(); // Приводим к миллисекундам
                                        const daysLeft = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));

                                        return daysLeft > 0 ? `${user?.user?.stripe_subscription_status}, ${daysLeft} day${daysLeft === 1 ? '' : 's'} left` : "Expired";
                                    })()}
                                )
                                    </>)}
                            </td>
                        </tr>

                        <tr>
                            <td className="text-start  small text-muted fst-italic">
                                {user?.user?.tariff_plan === "FREE" ? "Free plan start:" : "Subscription start:"}
                            </td>
                            <td className="text-start  small text-muted">
                                {user?.user?.tariff_plan === "FREE"
                                    ? new Date(user?.user?.registration_date * 60 * 1000).toLocaleString("en-US", {
                                        month: "2-digit", day: "2-digit", year: "numeric",
                                        hour: "2-digit", minute: "2-digit", hour12: true
                                    })
                                    : new Date(user?.user?.stripe_subscription_start_date).toLocaleString("en-US", {
                                        month: "2-digit", day: "2-digit", year: "numeric",
                                        hour: "2-digit", minute: "2-digit", hour12: true
                                    })}
                            </td>
                        </tr>

                        <tr>
                            <td className="text-start  small text-muted fst-italic">
                                {user?.user?.tariff_plan === "FREE" ? "Plan expired:" : "Subscription end:"}
                            </td>
                            <td className="text-start  small text-muted">{user?.user?.tariff_plan === "FREE" ? "Never end" : new Date(user?.user?.stripe_subscription_end_date).toLocaleString("en-US", {
                                month: "2-digit", day: "2-digit", year: "numeric",
                                hour: "2-digit", minute: "2-digit", hour12: true
                            })}</td>
                        </tr>

                        <tr>
                            <td className="text-start  small text-muted fst-italic">Book Limit / Rating:</td>
                            <td className="text-start small text-muted">
                                 {(() => {
                                    switch (user?.user?.tariff_plan) {
                                        case "FREE":
                                            return "One / ";
                                        case "ESSENTIAL":
                                            return "Five / ";
                                        case "PREMIUM":
                                            return "Fifty / ";
                                        case "MAXIMUM":
                                            return "500 / ";
                                        default:
                                            return "Unknown plan"; // Для непредвиденных случаев
                                    }
                                }) ()}
                                {reviewerData?.averageBookRating || ''} {[...Array(5)].map((_, index) => {
                            const rating = reviewerData?.averageBookRating || '';
                            let icon = 'star';

                            if (index + 1 <= rating) {
                                icon = 'star-fill';
                            } else if (index + 0.5 <= rating) {
                                icon = 'star-half';
                            }

                            return (
                                <i
                                    key={index}
                                    className={`bi bi-${icon}`}
                                    style={{
                                        color: 'gold',
                                        fontSize: '14px',
                                        marginRight: '3px'
                                    }}
                                ></i>
                            );
                        })} ({reviewerData?.bookRatingsCount === 1 ? '1 Rating' : `${reviewerData?.bookRatingsCount || 0} Ratings`})
                            </td>

                        </tr>

                        <tr>
                            <td className="text-start  small text-muted fst-italic">Assign Limit / Rating:</td>
                            <td className="text-start  small text-muted">{user?.user?.assignment_limit} / {reviewerData?.averageAssignRating || ''} {[...Array(5)].map((_, index) => {
                            const rating = reviewerData?.averageAssignRating || '';
                            let icon = 'star';

                            if (index + 1 <= rating) {
                                icon = 'star-fill';
                            } else if (index + 0.5 <= rating) {
                                icon = 'star-half';
                            }

                            return (
                                <i
                                    key={index}
                                    className={`bi bi-${icon}`}
                                    style={{
                                        color: 'gold',
                                        fontSize: '14px',
                                        marginRight: '3px'
                                    }}
                                ></i>
                            );
                        })} ({reviewerData?.assignRatingsCount === 1 ? '1 Rating' : `${reviewerData?.assignRatingsCount || 0} Ratings`})
                            </td>
                        </tr>

                        <tr>
                            <td className="text-start small text-muted fst-italic">Review Quality:</td>
                            <td className="text-start small text-muted">{reviewerData?.averageReviewerRating || ''} {[...Array(5)].map((_, index) => {
                                const rating = reviewerData?.averageReviewerRating || '';
                                let icon = 'star';

                                if (index + 1 <= rating) {
                                    icon = 'star-fill';
                                } else if (index + 0.5 <= rating) {
                                    icon = 'star-half';
                                }

                                return (
                                    <i
                                        key={index}
                                        className={`bi bi-${icon}`}
                                        style={{
                                            color: 'gold',
                                            fontSize: '14px',
                                            marginRight: '3px'
                                        }}
                                    ></i>
                                );
                            })} ({reviewerData?.reviewerRatingsCount === 1 ? '1 Rating' : `${reviewerData?.reviewerRatingsCount || 0} Ratings`})
                            </td>
                        </tr>
                        <tr>
                            <td className="text-start  small text-muted fst-italic">Trust Score:</td>
                            <td className="text-start  small text-muted">{user?.user?.level_of_trust} Points</td>
                        </tr>
                        </tbody>
                    </Table>



                </Col>
                </>
                                </Row>
                    </Card> */}

                    {!!settings?.dashboard_rdp_access_visible && <ColRdpAccess />}
                    <DiscountBonusOffers />
                    <DashboardActivity />



                            {/*}
                            <Card className=" rounded shadow-lg w-100 mt-2">
                    <Row className="g-1 ">
                {!loading ? (
                    <>
                        <Col md={12} className="mt-0 p-3 small">
                            <h5 className="text-muted fst-italic">Current & Upcoming Discount and Bonus Offers:</h5>
                            <Table striped bordered hover size="sm" className="align-middle">
                                <thead>
                                <tr>
                                    <th className="text-muted fst-italic">Offer Type</th>


                                    <th className="text-muted fst-italic">Category</th>
                                    <th className="text-muted fst-italic">Label</th>
                                    <th className="text-muted fst-italic">Value</th>
                                    <th className="text-muted fst-italic">Start Date</th>
                                    <th className="text-muted fst-italic">End Date</th>
                                    <th className="text-muted fst-italic">Status</th>
                                    <th className="text-muted fst-italic">Time Left</th>
                                </tr>
                                </thead>
                                <tbody>
                                {[
                                    ...(discountedBooks || []).map((item, index) => ({
                                        time: "run",
                                        up_run: (
                                            <>
                                                <FaHourglassHalf
                                                    className="text-success m-1 animate__animated animate__rotateIn animate__infinite"
                                                    size={12}
                                                />{" "}
                                                Running...
                                            </>
                                        ),
                                        type: "Placement Discount",
                                        category: "Book Type",
                                        label: item.type_name,
                                        value: `-${item.book_type_promotion_price_discount} V`,
                                        startDate: item.book_type_promotion_price_discount_start_date,
                                        endDate: item.book_type_promotion_price_discount_end_date
                                    })),

                                    ...(discountedAssignments || []).map((item, index) => ({
                                        time: "run",
                                        up_run: (
                                            <>
                                                <FaHourglassHalf
                                                    className="text-success m-1 animate__animated animate__rotateIn animate__infinite"
                                                    size={12}
                                                />{" "}
                                                Running...
                                            </>
                                        ),
                                        type: "Placement Discount",
                                        category: "Assignment Type",
                                        label: item.ass_type_name,
                                        value: `-${item.promotion_ass_price_discount} V`,
                                        startDate: item.promotion_ass_price_discount_start_date,
                                        endDate: item.promotion_ass_price_discount_end_date
                                    })),

                                    ...(discountedPrices || []).map((item, index) => ({
                                        time: "run",
                                        up_run: (
                                            <>
                                                <FaHourglassHalf
                                                    className="text-success m-1 animate__animated animate__rotateIn animate__infinite"
                                                    size={12}
                                                />{" "}
                                                Running...
                                            </>
                                        ),
                                        type: "Placement Discount",
                                        category: "Price Type",
                                        label: item.price,
                                        value: `-${item.promotion_price_discount} V`,
                                        startDate: item.promotion_price_discount_start_date,
                                        endDate: item.promotion_price_discount_end_date
                                    })),

                                    ...(bonusBooks || []).map((item, index) => ({
                                        time: "run",
                                        up_run: (
                                            <>
                                                <FaHourglassHalf
                                                    className="text-success m-1 animate__animated animate__rotateIn animate__infinite"
                                                    size={12}
                                                />{" "}
                                                Running...
                                            </>
                                        ),
                                        type: "Taking Bonus",
                                        category: "Book Type",
                                        label: item.type_name,
                                        value: `+${item.book_type_promotion_price_bonus} V`,
                                        startDate: item.book_type_promotion_price_bonus_start_date,
                                        endDate: item.book_type_promotion_price_bonus_end_date
                                    })),

                                    ...(bonusAssignments || []).map((item, index) => ({
                                        time: "run",
                                        up_run: (
                                            <>
                                                <FaHourglassHalf
                                                    className="text-success m-1 animate__animated animate__rotateIn animate__infinite"
                                                    size={12}
                                                />{" "}
                                                Running...
                                            </>
                                        ),
                                        type: "Taking Bonus",
                                        category: "Assignment Type",
                                        label: item.ass_type_name,
                                        value: `+${item.promotion_ass_price_bonus} V`,
                                        startDate: item.promotion_ass_price_bonus_start_date,
                                        endDate: item.promotion_ass_price_bonus_end_date
                                    })),

                                    ...(bonusPrices || []).map((item, index) => ({
                                        time: "run",
                                        up_run: (
                                            <>
                                                <FaHourglassHalf
                                                    className="text-success m-1 animate__animated animate__rotateIn animate__infinite"
                                                    size={12}
                                                />{" "}
                                                Running...
                                            </>
                                        ),
                                        type: "Taking Bonus",
                                        category: "Price Type",
                                        label: item.price,
                                        value: `+${item.promotion_price_bonus} V`,
                                        startDate: item.promotion_price_bonus_start_date,
                                        endDate: item.promotion_price_bonus_end_date
                                    })),
                                    ...(upcomingDiscountedBooks || []).map((item, index) => ({
                                        time: "up",
                                        up_run: (
                                            <>
                                                <FaClock
                                                    className="text-success m-1"
                                                    size={12}
                                                />{" "}
                                                Upcoming
                                            </>
                                        ),
                                        type: "Placement Discount",
                                        category: "Book Type",
                                        label: item.type_name,
                                        value: `-${item.book_type_promotion_price_discount} V`,
                                        startDate: item.book_type_promotion_price_discount_start_date,
                                        endDate: item.book_type_promotion_price_discount_end_date
                                    })),
                                    ...(upcomingDiscountedAssignments || []).map((item, index) => ({
                                        time: "up",
                                        up_run: (
                                            <>
                                                <FaClock
                                                    className="text-success m-1"
                                                    size={12}
                                                />{" "}
                                                Upcoming
                                            </>
                                        ),
                                        type: "Placement Discount",
                                        category: "Assignment Type",
                                        label: item.ass_type_name,
                                        value: `-${item.promotion_ass_price_discount} V`,
                                        startDate: item.promotion_ass_price_discount_start_date,
                                        endDate: item.promotion_ass_price_discount_end_date
                                    })),
                                    ...(upcomingDiscountedPrices || []).map((item, index) => ({
                                        time: "up",
                                        up_run: (
                                            <>
                                                <FaClock
                                                    className="text-success m-1"
                                                    size={12}
                                                />{" "}
                                                Upcoming
                                            </>
                                        ),
                                        type: "Placement Discount",
                                        category: "Price Type",
                                        label: item.price,
                                        value: `-${item.promotion_price_discount} V`,
                                        startDate: item.promotion_price_discount_start_date,
                                        endDate: item.promotion_price_discount_end_date
                                    })),
                                    ...(upcomingBonusBooks || []).map((item, index) => ({
                                        time: "up",
                                        up_run: (
                                            <>
                                                <FaClock
                                                    className="text-success m-1"
                                                    size={12}
                                                />{" "}
                                                Upcoming
                                            </>
                                        ),
                                        type: "Taking Bonus",
                                        category: "Book Type",
                                        label: item.type_name,
                                        value: `+${item.book_type_promotion_price_bonus} V`,
                                        startDate: item.book_type_promotion_price_bonus_start_date,
                                        endDate: item.book_type_promotion_price_bonus_end_date
                                    })),
                                    ...(upcomingBonusAssignments || []).map((item, index) => ({
                                        time: "up",
                                        up_run: (
                                            <>
                                                <FaClock
                                                    className="text-success m-1"
                                                    size={12}
                                                />{" "}
                                                Upcoming
                                            </>
                                        ),
                                        type: "Taking Bonus",
                                        category: "Assignment Type",
                                        label: item.ass_type_name,
                                        value: `+${item.promotion_ass_price_bonus} V`,
                                        startDate: item.promotion_ass_price_bonus_start_date,
                                        endDate: item.promotion_ass_price_bonus_end_date
                                    })),
                                    ...(upcomingBonusPrices || []).map((item, index) => ({
                                        time: "up",
                                        up_run: (
                                            <>
                                                <FaClock
                                                    className="text-success m-1"
                                                    size={12}
                                                />{" "}
                                                Upcoming
                                            </>
                                        ),
                                        type: "Taking Bonus",
                                        category: "Price Type",
                                        label: item.price,
                                        value: `+${item.promotion_price_bonus} V`,
                                        startDate: item.promotion_price_bonus_start_date,
                                        endDate: item.promotion_price_bonus_end_date
                                    })),

                                ].map((row, index) => (
                                    <tr key={index}>
                                        <td className="text-start text-muted fst-italic">
                                            {row.type}
                                        </td>


                                        <td className="text-start text-muted fst-italic">
                                            {row.category}
                                        </td>
                                        <td className="text-start text-muted fst-italic">
                                            {row.label}
                                        </td>
                                        <td className={`text-start ${row.type === "Bonus" ? "text-primary" : "text-success"}`}>
                                            {row.value}
                                        </td>
                                        <td className="text-start text-muted">
                                            {(() => {
                                                const date = new Date(row.startDate * 60000);
                                                const day = date.getDate().toString().padStart(2, '0');
                                                const month = date.toLocaleString("en-US", { month: "short" });
                                                const year = date.getFullYear();
                                                return `${day}-${month}-${year}`;
                                            })()}
                                        </td>

                                        <td className="text-start text-muted">
                                            {(() => {
                                                const date = new Date(row.endDate * 60000);
                                                const day = date.getDate().toString().padStart(2, '0');
                                                const month = date.toLocaleString("en-US", { month: "short" });
                                                const year = date.getFullYear();
                                                return `${day}-${month}-${year}`;
                                            })()}
                                        </td>
                                        <td className="text-start text-muted fst-italic">
                                            {row.up_run}
                                        </td>
                                        <td className="text-start fst-italic text-danger">
                                            {row.time === "up" ? "Starts in " : "Ends in "}
                                            {row.time === "up" ? formatTimeLeft(row.startDate) : formatTimeLeft(row.endDate)}


                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </Col>
                    </>
                ) : (<><h5 className="text-muted fw-bold">Loading Discount and Bonus Offers...</h5></>)}


                    </Row>
        </Card>
        */}


                    {!isMobile && (<>
                            <Card className="p-3 w-100 mt-2">
                                <div className="d-flex flex-column flex-md-row flex-md-nowrap justify-content-between align-items-start align-items-md-center gap-2">

                                <Col md={5} className="d-flex align-items-center justify-content-start p-2">
                                        <div className="text-muted small">
                                            <h5 className="mb-3 fst-italic">User Distribution by Amazon Domain</h5>
                                            <p>
                                                This pie chart shows the distribution of registered users based on the country-specific Amazon domains they provided.
                                                Each segment represents the percentage of users who have selected a specific regional Amazon website, such as <strong>www.amazon.com</strong> or <strong>www.amazon.co.uk</strong>.
                                            </p>
                                            <p>
                                                This information helps us better understand where our users are located and which marketplaces are most popular among them.
                                            </p>
                                            <p className="mt-3">
                                                <strong>Note:</strong> If you apply country filters when creating assignments, keep in mind that the speed at which readers are found is directly proportional to this distribution.
                                                To ensure the fastest possible matching, we recommend avoiding country filters unless absolutely necessary.
                                            </p>
                                        </div>
                                    </Col>
                                    <Col md={7}>
                                        <div style={{ height: 370, width: '100%' }} className="p-3">
                                            <UsersByCountryPie />
                                        </div>
                                    </Col>

                            </div>
                            </Card>

            {/* Свитчер выбора периода
            <Row className=" p-2 ms-0 mt-2 bg-white rounded">
                <Col mb={12} className="d-flex justify-content-center">
                    <ToggleButtonGroup type="radio" name="chartView" value={chartView} onChange={setChartView}>
                        <ToggleButton
                            id="30days"
                            variant={chartView === "30days" ? "success" : "outline-success"}
                            value="30days"
                        >
                            Trends Last 30 Days
                        </ToggleButton>
                        <ToggleButton
                            id="120days"
                            variant={chartView === "120days" ? "success" : "outline-success"}
                            value="120days"
                        >
                            Trends Last 120 Days
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Col>
            </Row>


                            <Card className=" rounded shadow-lg p-3 w-100 mt-2">

            {/* Контент в зависимости от выбранного периода
            {chartView === "30days" ? (
                <Row className="g-0 ms-0 mt-0">
                    <Col mb={12}>
                        <div className="text-start mt-0">
                            <h5 className="text-muted fw-bold">Financial & Trust Level Trends (Last 30 Days)</h5>
                            <p className="text-muted small">
                                Tracking Balance, Earnings, Expenses & Trust Growth Over Time
                            </p>
                        </div>
                        <BumpChartDemo />
                    </Col>
                </Row>
            ) : (
                <Row className="g-0 ms-0 mt-0">
                    <Col mb={12}>
                        <div className="text-start mt-0">
                            <h5 className="text-muted fw-bold">Financial & Trust Level Trends (Last 120 Days, Grouped into 3-Day Chunks)</h5>
                            <p className="text-muted small">
                                Tracking Balance, Earnings, Expenses & Trust Growth Over Time
                            </p>
                        </div>
                        <BumpChartChunk3 />
                    </Col>
                </Row>
            )}
                            </Card>
*/}

                </>
            )}
                </>)}

          </div>

    );
};


export default DashBoardMain;
